import * as signalR from '@microsoft/signalr';
import { useConnectionStore } from '../../stores/connectionStore';
import { storage } from '../platform';
import { URL } from '../env';
import { reconnectSyncManager } from './reconnectSyncManager';
import { signalREventDispatcher, type SignalREventName } from './signalREventDispatcher';

class SignalRService {
  private connection?: signalR.HubConnection;
  private connectionPromise: Promise<void> | null = null;
  private handlersRegistered = false;

  private registerEventHandlers(): void {
    if (!this.connection || this.handlersRegistered) return;

    (Object.entries(signalREventDispatcher) as [SignalREventName, (...args: unknown[]) => void][]).forEach(
      ([eventName, handler]) => {
        this.connection!.on(eventName, handler);
      },
    );

    this.connection.onreconnecting(() => {
      useConnectionStore.getState().setStatus('reconnecting');
    });

    this.connection.onreconnected(async () => {
      useConnectionStore.getState().setStatus('connected');
      try {
        await reconnectSyncManager.syncAfterReconnect();
      } catch (err) {
        console.error('SignalR reconnect sync failed:', err);
      }
    });

    this.connection.onclose(() => {
      useConnectionStore.getState().setStatus('disconnected');
      this.handlersRegistered = false;
    });

    this.handlersRegistered = true;
  }

  async start(): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      return;
    }

    this.connectionPromise = (async () => {
      const token = await storage.getItem('token');
      if (token == null) {
        throw new Error('SignalR: 缺少 auth token，無法建立連線');
      }

      if (!this.connection || this.connection.state === signalR.HubConnectionState.Disconnected) {
        this.handlersRegistered = false;
        this.connection = new signalR.HubConnectionBuilder()
          .withUrl(`${URL}/matchHub`, {
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
            accessTokenFactory: async () => (await storage.getItem('token')) ?? '',
          })
          .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
          .configureLogging(signalR.LogLevel.Warning)
          .build();
      }

      try {
        if (this.connection.state === signalR.HubConnectionState.Disconnected) {
          useConnectionStore.getState().setStatus('connecting');
          await this.connection.start();
          this.registerEventHandlers();
          useConnectionStore.getState().setStatus('connected');
          console.log('SignalR Connected');
        }
      } catch (err) {
        useConnectionStore.getState().setStatus('disconnected');
        useConnectionStore.getState().setError(String(err));
        console.error('SignalR Connection Error:', err);
        throw err;
      }
    })();

    return this.connectionPromise.finally(() => {
      this.connectionPromise = null;
    });
  }

  async stop(): Promise<void> {
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      await this.connection.stop();
      console.log('SignalR Disconnected');
    }
    this.handlersRegistered = false;
    useConnectionStore.getState().setStatus('disconnected');
  }

  /** 允許組件級額外訂閱（如 CandleChart 的 SyncGameStart） */
  on(eventName: string, handler: (...args: any[]) => void): void {
    if (!this.connection) {
      console.warn(`SignalR (on) connection not ready: ${eventName}`);
      return;
    }
    this.connection.on(eventName, handler);
  }

  off(eventName: string, handler: (...args: any[]) => void): void {
    this.connection?.off(eventName, handler);
  }

  async invoke(methodName: string, ...args: unknown[]): Promise<unknown> {
    try {
      await this.start();
    } catch (e) {
      console.warn(`SignalR 無法連線，略過 invoke: ${methodName}`);
      return Promise.reject(e);
    }

    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      const state = this.connection?.state;
      console.warn(`SignalR invoke 失敗 (${methodName}): 連線狀態為 ${state}`);
      return Promise.reject(new Error(`SignalR Not Connected: ${state}`));
    }

    try {
      return await this.connection.invoke(methodName, ...args);
    } catch (err) {
      console.error(`SignalR invoke error (${methodName}):`, err);
      throw err;
    }
  }
}

export const signalRService = new SignalRService();
